/**
 * ShopScene.ts - Shop interface for buying and selling items
 * FF1-style shop menu with item lists, prices, and purchase confirmation
 */

import { Scene, GameInput, GameKey, MenuItem } from './types/engine';
import { Renderer } from './Renderer';
import { BuildingType } from './data/TownData';

export enum ShopMode {
  MAIN_MENU = 'MAIN_MENU',
  BUY_MENU = 'BUY_MENU',
  SELL_MENU = 'SELL_MENU',
  CONFIRM = 'CONFIRM',
}

export interface ShopItem {
  id: number;
  name: string;
  description: string;
  price: number;
  type: 'weapon' | 'armor' | 'item' | 'spell';
  icon?: string;
}

export class ShopScene implements Scene {
  private renderer: Renderer;

  // Shop state
  private shopType: BuildingType;
  private shopName: string;
  private inventory: ShopItem[];
  private playerGil: number;
  private playerInventory: ShopItem[];

  // Menu state
  private mode: ShopMode = ShopMode.MAIN_MENU;
  private selectedIndex: number = 0;
  private scrollOffset: number = 0;
  private readonly MAX_VISIBLE_ITEMS = 8;

  // Confirmation state
  private confirmingItem: ShopItem | null = null;
  private confirmQuantity: number = 1;

  // Callbacks
  private onCloseCallback: (() => void) | null = null;
  private onPurchaseCallback: ((item: ShopItem, quantity: number) => void) | null = null;
  private onSellCallback: ((item: ShopItem, quantity: number) => void) | null = null;

  // Input cooldown
  private inputCooldown: number = 0;
  private readonly INPUT_COOLDOWN_MS = 150;

  constructor(
    ctx: CanvasRenderingContext2D,
    shopType: BuildingType,
    shopName: string,
    inventory: number[],
    playerGil: number = 1000,
    playerInventory: ShopItem[] = []
  ) {
    this.renderer = new Renderer(ctx);
    this.shopType = shopType;
    this.shopName = shopName;
    this.playerGil = playerGil;
    this.playerInventory = playerInventory;

    // Convert inventory IDs to ShopItems (mock data for now)
    this.inventory = this.loadShopInventory(inventory);
  }

  // =========================================================================
  // Scene Interface
  // =========================================================================

  init(): void {
    console.log(`[ShopScene] Opened ${this.shopName}`);
    this.mode = ShopMode.MAIN_MENU;
    this.selectedIndex = 0;
  }

  update(deltaTime: number): void {
    // Update input cooldown
    if (this.inputCooldown > 0) {
      this.inputCooldown -= deltaTime;
    }
  }

  render(ctx: CanvasRenderingContext2D): void {
    this.renderer.setContext(ctx);

    // Clear screen
    this.renderer.clear('#000000');

    // Render based on current mode
    switch (this.mode) {
      case ShopMode.MAIN_MENU:
        this.renderMainMenu();
        break;
      case ShopMode.BUY_MENU:
        this.renderBuyMenu();
        break;
      case ShopMode.SELL_MENU:
        this.renderSellMenu();
        break;
      case ShopMode.CONFIRM:
        this.renderConfirmation();
        break;
    }

    // Always show gil in top-right
    this.renderGilDisplay();
  }

  handleInput(input: GameInput): void {
    // Don't process input during cooldown
    if (this.inputCooldown > 0) {
      return;
    }

    // Handle input based on mode
    switch (this.mode) {
      case ShopMode.MAIN_MENU:
        this.handleMainMenuInput(input);
        break;
      case ShopMode.BUY_MENU:
        this.handleBuyMenuInput(input);
        break;
      case ShopMode.SELL_MENU:
        this.handleSellMenuInput(input);
        break;
      case ShopMode.CONFIRM:
        this.handleConfirmInput(input);
        break;
    }
  }

  cleanup(): void {
    // Nothing to cleanup
  }

  // =========================================================================
  // Input Handling
  // =========================================================================

  private handleMainMenuInput(input: GameInput): void {
    // Navigate menu
    if (input.justPressed.has(GameKey.UP)) {
      this.selectedIndex = Math.max(0, this.selectedIndex - 1);
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    if (input.justPressed.has(GameKey.DOWN)) {
      this.selectedIndex = Math.min(2, this.selectedIndex + 1);
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    // Select option
    if (input.justPressed.has(GameKey.A)) {
      switch (this.selectedIndex) {
        case 0: // Buy
          this.mode = ShopMode.BUY_MENU;
          this.selectedIndex = 0;
          this.scrollOffset = 0;
          break;
        case 1: // Sell
          this.mode = ShopMode.SELL_MENU;
          this.selectedIndex = 0;
          this.scrollOffset = 0;
          break;
        case 2: // Exit
          if (this.onCloseCallback) {
            this.onCloseCallback();
          }
          break;
      }
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    // Cancel - close shop
    if (input.justPressed.has(GameKey.B)) {
      if (this.onCloseCallback) {
        this.onCloseCallback();
      }
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }
  }

  private handleBuyMenuInput(input: GameInput): void {
    const itemCount = this.inventory.length;

    // Navigate list
    if (input.justPressed.has(GameKey.UP)) {
      this.selectedIndex = Math.max(0, this.selectedIndex - 1);
      this.updateScrollOffset(itemCount);
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    if (input.justPressed.has(GameKey.DOWN)) {
      this.selectedIndex = Math.min(itemCount - 1, this.selectedIndex + 1);
      this.updateScrollOffset(itemCount);
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    // Select item to buy
    if (input.justPressed.has(GameKey.A)) {
      const item = this.inventory[this.selectedIndex];
      if (item && this.playerGil >= item.price) {
        this.confirmingItem = item;
        this.confirmQuantity = 1;
        this.mode = ShopMode.CONFIRM;
      } else {
        // Show "Not enough gil" message (could be a separate state)
        console.log('[ShopScene] Not enough gil!');
      }
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    // Cancel - back to main menu
    if (input.justPressed.has(GameKey.B)) {
      this.mode = ShopMode.MAIN_MENU;
      this.selectedIndex = 0;
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }
  }

  private handleSellMenuInput(input: GameInput): void {
    const itemCount = this.playerInventory.length;

    if (itemCount === 0) {
      // No items to sell, go back
      if (input.justPressed.has(GameKey.B)) {
        this.mode = ShopMode.MAIN_MENU;
        this.selectedIndex = 0;
        this.inputCooldown = this.INPUT_COOLDOWN_MS;
      }
      return;
    }

    // Navigate list
    if (input.justPressed.has(GameKey.UP)) {
      this.selectedIndex = Math.max(0, this.selectedIndex - 1);
      this.updateScrollOffset(itemCount);
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    if (input.justPressed.has(GameKey.DOWN)) {
      this.selectedIndex = Math.min(itemCount - 1, this.selectedIndex + 1);
      this.updateScrollOffset(itemCount);
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    // Select item to sell
    if (input.justPressed.has(GameKey.A)) {
      const item = this.playerInventory[this.selectedIndex];
      if (item) {
        this.confirmingItem = item;
        this.confirmQuantity = 1;
        this.mode = ShopMode.CONFIRM;
      }
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    // Cancel - back to main menu
    if (input.justPressed.has(GameKey.B)) {
      this.mode = ShopMode.MAIN_MENU;
      this.selectedIndex = 0;
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }
  }

  private handleConfirmInput(input: GameInput): void {
    // Navigate Yes/No
    if (input.justPressed.has(GameKey.LEFT) || input.justPressed.has(GameKey.RIGHT)) {
      this.selectedIndex = this.selectedIndex === 0 ? 1 : 0;
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    // Confirm
    if (input.justPressed.has(GameKey.A)) {
      if (this.selectedIndex === 0 && this.confirmingItem) {
        // Yes - execute transaction
        if (this.mode === ShopMode.CONFIRM) {
          // Was buying
          if (this.onPurchaseCallback) {
            this.onPurchaseCallback(this.confirmingItem, this.confirmQuantity);
          }
        }
        // Return to buy menu
        this.mode = ShopMode.BUY_MENU;
        this.selectedIndex = 0;
      } else {
        // No - cancel
        this.mode = ShopMode.BUY_MENU;
        this.selectedIndex = 0;
      }
      this.confirmingItem = null;
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }

    // Cancel
    if (input.justPressed.has(GameKey.B)) {
      this.mode = ShopMode.BUY_MENU;
      this.selectedIndex = 0;
      this.confirmingItem = null;
      this.inputCooldown = this.INPUT_COOLDOWN_MS;
      return;
    }
  }

  private updateScrollOffset(_itemCount: number): void {
    // Keep selected item visible
    if (this.selectedIndex < this.scrollOffset) {
      this.scrollOffset = this.selectedIndex;
    } else if (this.selectedIndex >= this.scrollOffset + this.MAX_VISIBLE_ITEMS) {
      this.scrollOffset = this.selectedIndex - this.MAX_VISIBLE_ITEMS + 1;
    }
  }

  // =========================================================================
  // Rendering
  // =========================================================================

  private renderMainMenu(): void {
    // Window background
    this.renderer.drawWindow(32, 60, 192, 100);

    // Shop name
    this.renderer.drawText(this.shopName, 128, 70, {
      font: 'ff1',
      fontSize: 10,
      color: '#ffffff',
      align: 'center',
      shadow: true,
    });

    // Menu options
    const options = ['BUY', 'SELL', 'EXIT'];
    options.forEach((option, index) => {
      const y = 95 + index * 20;
      const color = index === this.selectedIndex ? '#ffff00' : '#ffffff';

      // Draw cursor
      if (index === this.selectedIndex) {
        this.renderer.drawCursor(50, y + 2);
      }

      this.renderer.drawText(option, 70, y, {
        font: 'ff1',
        fontSize: 8,
        color,
        shadow: true,
      });
    });
  }

  private renderBuyMenu(): void {
    // Main window
    this.renderer.drawWindow(16, 40, 224, 160);

    // Title
    this.renderer.drawText('BUY', 128, 48, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
      align: 'center',
      shadow: true,
    });

    // Item list
    const startIndex = this.scrollOffset;
    const endIndex = Math.min(startIndex + this.MAX_VISIBLE_ITEMS, this.inventory.length);

    for (let i = startIndex; i < endIndex; i++) {
      const item = this.inventory[i];
      const y = 70 + (i - startIndex) * 16;
      const isSelected = i === this.selectedIndex;
      const color = isSelected ? '#ffff00' : '#ffffff';

      // Draw cursor
      if (isSelected) {
        this.renderer.drawCursor(24, y + 2);
      }

      // Item name
      this.renderer.drawText(item.name, 40, y, {
        font: 'ff1',
        fontSize: 8,
        color,
        shadow: true,
      });

      // Price
      this.renderer.drawText(`${item.price}G`, 200, y, {
        font: 'ff1',
        fontSize: 8,
        color,
        align: 'right',
        shadow: true,
      });
    }

    // Show description of selected item
    if (this.selectedIndex >= 0 && this.selectedIndex < this.inventory.length) {
      const item = this.inventory[this.selectedIndex];
      this.renderer.drawWindow(16, 210, 224, 20);
      this.renderer.drawText(item.description, 24, 216, {
        font: 'ff1',
        fontSize: 6,
        color: '#ffffff',
        shadow: true,
      });
    }

    // Scroll indicators
    if (this.scrollOffset > 0) {
      this.renderer.drawText('▲', 230, 70, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffffff',
      });
    }
    if (endIndex < this.inventory.length) {
      this.renderer.drawText('▼', 230, 190, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffffff',
      });
    }
  }

  private renderSellMenu(): void {
    // Similar to buy menu but for player inventory
    this.renderer.drawWindow(16, 40, 224, 160);

    this.renderer.drawText('SELL', 128, 48, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
      align: 'center',
      shadow: true,
    });

    if (this.playerInventory.length === 0) {
      this.renderer.drawText('Nothing to sell', 128, 120, {
        font: 'ff1',
        fontSize: 8,
        color: '#ffffff',
        align: 'center',
        shadow: true,
      });
      return;
    }

    // Item list
    const startIndex = this.scrollOffset;
    const endIndex = Math.min(startIndex + this.MAX_VISIBLE_ITEMS, this.playerInventory.length);

    for (let i = startIndex; i < endIndex; i++) {
      const item = this.playerInventory[i];
      const y = 70 + (i - startIndex) * 16;
      const isSelected = i === this.selectedIndex;
      const color = isSelected ? '#ffff00' : '#ffffff';

      if (isSelected) {
        this.renderer.drawCursor(24, y + 2);
      }

      this.renderer.drawText(item.name, 40, y, {
        font: 'ff1',
        fontSize: 8,
        color,
        shadow: true,
      });

      // Sell price (half of buy price)
      const sellPrice = Math.floor(item.price / 2);
      this.renderer.drawText(`${sellPrice}G`, 200, y, {
        font: 'ff1',
        fontSize: 8,
        color,
        align: 'right',
        shadow: true,
      });
    }
  }

  private renderConfirmation(): void {
    if (!this.confirmingItem) return;

    // Confirmation window
    this.renderer.drawWindow(48, 90, 160, 60);

    const isBuying = this.mode === ShopMode.CONFIRM;
    const action = isBuying ? 'Buy' : 'Sell';
    const price = isBuying ? this.confirmingItem.price : Math.floor(this.confirmingItem.price / 2);

    this.renderer.drawText(`${action} ${this.confirmingItem.name}`, 128, 100, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
      align: 'center',
      shadow: true,
    });

    this.renderer.drawText(`for ${price}G?`, 128, 115, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
      align: 'center',
      shadow: true,
    });

    // Yes/No options
    const yesColor = this.selectedIndex === 0 ? '#ffff00' : '#ffffff';
    const noColor = this.selectedIndex === 1 ? '#ffff00' : '#ffffff';

    if (this.selectedIndex === 0) {
      this.renderer.drawCursor(70, 132);
    }
    this.renderer.drawText('YES', 85, 130, {
      font: 'ff1',
      fontSize: 8,
      color: yesColor,
      shadow: true,
    });

    if (this.selectedIndex === 1) {
      this.renderer.drawCursor(140, 132);
    }
    this.renderer.drawText('NO', 155, 130, {
      font: 'ff1',
      fontSize: 8,
      color: noColor,
      shadow: true,
    });
  }

  private renderGilDisplay(): void {
    // Gil window in top-right
    this.renderer.drawWindow(160, 8, 88, 24);
    this.renderer.drawText('GIL', 168, 14, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffffff',
      shadow: true,
    });
    this.renderer.drawText(this.playerGil.toString(), 240, 14, {
      font: 'ff1',
      fontSize: 8,
      color: '#ffff00',
      align: 'right',
      shadow: true,
    });
  }

  // =========================================================================
  // Data Loading
  // =========================================================================

  private loadShopInventory(inventoryIds: number[]): ShopItem[] {
    // Mock data - in real implementation, this would fetch from ItemData contract
    const mockItems: Record<number, ShopItem> = {
      1: { id: 1, name: 'Rapier', description: 'A light, swift blade', price: 10, type: 'weapon' },
      2: { id: 2, name: 'Iron Sword', description: 'Standard sword', price: 50, type: 'weapon' },
      3: { id: 3, name: 'Scimitar', description: 'Curved blade', price: 200, type: 'weapon' },
      4: { id: 4, name: 'Falchion', description: 'Heavy sword', price: 450, type: 'weapon' },
      5: { id: 5, name: 'Broadsword', description: 'Wide blade', price: 800, type: 'weapon' },
      50: { id: 50, name: 'Leather Armor', description: 'Basic protection', price: 50, type: 'armor' },
      51: { id: 51, name: 'Chain Mail', description: 'Metal links', price: 200, type: 'armor' },
      52: { id: 52, name: 'Iron Armor', description: 'Heavy armor', price: 800, type: 'armor' },
      101: { id: 101, name: 'Potion', description: 'Restores 50 HP', price: 50, type: 'item' },
      102: { id: 102, name: 'Hi-Potion', description: 'Restores 150 HP', price: 150, type: 'item' },
      103: { id: 103, name: 'Antidote', description: 'Cures poison', price: 75, type: 'item' },
      201: { id: 201, name: 'Fire', description: 'Basic fire spell', price: 100, type: 'spell' },
      202: { id: 202, name: 'Cure', description: 'Healing spell', price: 100, type: 'spell' },
      203: { id: 203, name: 'Ice', description: 'Basic ice spell', price: 100, type: 'spell' },
    };

    return inventoryIds
      .map((id) => mockItems[id])
      .filter((item) => item !== undefined) as ShopItem[];
  }

  // =========================================================================
  // Public API
  // =========================================================================

  onClose(callback: () => void): void {
    this.onCloseCallback = callback;
  }

  onPurchase(callback: (item: ShopItem, quantity: number) => void): void {
    this.onPurchaseCallback = callback;
  }

  onSell(callback: (item: ShopItem, quantity: number) => void): void {
    this.onSellCallback = callback;
  }

  setPlayerGil(gil: number): void {
    this.playerGil = gil;
  }

  setPlayerInventory(inventory: ShopItem[]): void {
    this.playerInventory = inventory;
  }

  /**
   * Load a new shop configuration (called when entering different shops)
   */
  loadShop(type: BuildingType, name: string, inventoryIds: number[]): void {
    this.shopType = type;
    this.shopName = name;
    this.inventory = this.loadShopInventory(inventoryIds);
    this.mode = ShopMode.MAIN_MENU;
    this.selectedIndex = 0;
    this.scrollOffset = 0;
    console.log(`[ShopScene] Loaded shop: ${name} with ${this.inventory.length} items`);
  }
}
